import { authenticatedUser } from "../../authentication/authModule";
import { Environment } from "../enums/Environment";
import { UserRole } from "../enums/UserRole";
import { WorkflowState } from "../enums/WorkflowState";
import User from "../User";
import WorkflowInstance from "../WorkflowInstance";
import AWSAccountConnectivityConfiguration from "./AWSAccountConnectivityConfiguration";
import AWSPortOpeningRequest from "./AWSPortOpeningRequests";
import CartaId from "./CartaId";
import Organization from "./Organization";

export default class AWSAccountRequest {

    id!: number;
    
    accountUsedFor!: string;

    environment!: Environment;

    accountName!: string;

    awsAccountNumber: string;

    appName!: string;

    createdAccountId!: string;

    workflowInstance!: WorkflowInstance;

    owner!: User;

    custodian!: User; 
    
    organization?: Organization; 

    cartaIds!: CartaId[];

    pocDurationDays: number;

    isManaged!: boolean;

    portOpeningRequests: AWSPortOpeningRequest[];

    internalOrder: string;

    isRetainedApp!: boolean;

    requiresEc2Instances: boolean;

    requiresPublicIpAddresses: boolean;

    requiresNativeContainerServices: boolean;

    requiresIsolation: boolean;

    requiresDMZ: boolean;

    numOfDmzPublicIpAddresses: string;

    vpcSizeId?: string;

    regionNames?: any;

    selected!: boolean;

    network: AWSAccountConnectivityConfiguration[];

    accountGroups?: Record<string, string[]>;

    pendingOwnershipTransfer?: {
        proposedOwner: User;
        initiator: User;
        proposedCustodian?: User;
    };

    isImported?: boolean;

    pocStartDate: Date;

    isTier0: boolean;

    isCyberCritical: boolean;

    constructor(obj: any) {
        Object.assign(this, obj);
        this.isImported = obj.isImported;
        this.workflowInstance = new WorkflowInstance(obj.workflowInstance);
        this.organization = obj.organizationalDetails?.lowestLevelOrganization 
            ? new Organization(obj.organizationalDetails.lowestLevelOrganization) 
            : undefined;
        
        this.internalOrder = obj.organizationalDetails?.internalOrder || '';
        this.accountUsedFor = obj.organizationalDetails?.techOrBusiness || '';
        this.owner = obj.owner;
        
        this.requiresEc2Instances = obj.accountConfig?.requiresEc2Instances || false;
        this.requiresPublicIpAddresses = obj.accountConfig?.requiresPublicIpAddresses || false;
        this.requiresNativeContainerServices = obj.accountConfig?.requiresNativeContainerServices || false;
        this.requiresIsolation = obj.requiresDedicatedVpc || false;
        this.regionNames = obj.accountConfig?.vpcRequirements?.regions?.map((region: any) => region.name);
        this.vpcSizeId = obj.accountConfig?.vpcRequirements?.vpcSize?.value;
        this.requiresDMZ = obj.requiresDmz || false;
        this.network = obj.accountConfig?.vpcRequirements?.vpcs?.map((net) => new AWSAccountConnectivityConfiguration(net)) || [];
        this.numOfDmzPublicIpAddresses = obj.accountConfig?.vpcRequirements?.numOfDmzPublicIpAddresses?.toString();
        this.cartaIds = [ new CartaId(obj.appName, obj.appId) ];
        
        this.accountGroups = this.parseAccountGroups(obj.groups?.assignments);

        this.pocStartDate = obj.pocStartDate;
        this.pocDurationDays = obj.pocDurationDays;
        this.isTier0 = obj.isTier0;
        this.isCyberCritical = obj.isCyberCritical;

        if (obj.pendingOwnershipTransfer) {
            this.pendingOwnershipTransfer = {
                proposedOwner: obj.pendingOwnershipTransfer.proposedOwner,
                initiator: obj.pendingOwnershipTransfer.initiator,
                proposedCustodian: obj.pendingOwnershipTransfer.proposedCustodian,
            };
        }
    }

    private parseAccountGroups(
        permissionSetAssignments: Record<string, Array<{id: string, isPendingApproval?: boolean, activeDirectoryInfo?: any}>> | undefined
    ): Record<string, string[]> {
        if (!permissionSetAssignments) {
            return {};
        }

        const result: Record<string, string[]> = {};
        
        for (const [permissionSet, groups] of Object.entries(permissionSetAssignments)) {
            // Skip empty keys
            if (!permissionSet || permissionSet.trim() === '') {
                continue;
            }
            
            if (!Array.isArray(groups) || groups.length === 0) {
                continue;
            }
            
            result[permissionSet] = groups
                .filter(g => g && g.id)
                .map(g => g.id);
        }

        return result;
    }

    isImportedAccount(): boolean {
        return this.isImported === true;
    }

    isRecentlyImportedAccount(): boolean {
        return this.isImportedAccount() && 
            (this.workflowInstance?.workflowInstanceStates?.length ?? 0) <= 1;
    }

 
    getGroupsForPermissionSet(permissionSet: string): string[] {
        return this.accountGroups?.[permissionSet] || [];
    }


    getFirstGroupForPermissionSet(permissionSet: string): string | undefined {
        return this.accountGroups?.[permissionSet]?.[0];
    }


    getAssignedPermissionSets(): string[] {
        if (!this.accountGroups) return [];
        return Object.keys(this.accountGroups).filter(
            permissionSet => (this.accountGroups![permissionSet]?.length ?? 0) > 0
        );
    }

    setEditable(select: boolean) {
        this.selected = select;
    }

    isVpcEditable(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState;
        return (
            (
                currentState?.value == WorkflowState.PendingAwsConnectivityDetails.value &&
                this.isManaged
            ) &&
            (
                authenticatedUser.isInRole(UserRole.SubscriptionAdmin) ||
                authenticatedUser.isInRole(UserRole.AWSConnectivityTechnicalImplementor)
            )
        );
    }


    canEditConnectivitySettings(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState;
        return (
            currentState?.value === WorkflowState.Completed.value &&
            this.isManaged &&
            authenticatedUser.isInRole(UserRole.AWSConnectivityTechnicalImplementor)
        );
    }

    isEditable(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState;
        return (
            (
                currentState?.value == WorkflowState.Completed.value
            ) &&
            (
                authenticatedUser.userId == this.owner?.id || 
                (this.custodian && authenticatedUser.userId == this.custodian.id)
                || authenticatedUser.isInRole(UserRole.AWSAccountAdmin)
            )
        );
    }

    canSubmitPortOpeningRequests(): boolean {
        return this.canHavePortOpeningRequests();
    }

    canSeePortOpeningRequests(): boolean {
        const allowedRoles = [UserRole.AWSPORReader];
        const userIsAllowed = authenticatedUser.isInAnyRole(allowedRoles) && this.isManaged;
        return userIsAllowed;
    }

    private canHavePortOpeningRequests(): boolean {
        return this.isManaged &&
        this.workflowInstance?.currentWorkflowInstanceState?.workflowState === WorkflowState.Completed;
    }

    canHaveAccountOwnerDecision(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState;
            return currentState == WorkflowState.PendingAccountOwnerApproval && (
                this.owner?.id == authenticatedUser.userId
            );
    }

    canHaveTechnicalDecision(): boolean {
        if (this.isPendingRenewalApproval()) return false;
        
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;
        return (
            (
                currentState == WorkflowState.PendingPlatformsArchitectApproval.value
                && authenticatedUser.isInRole(UserRole.AWSPlatformArchitect)
                
            ) ||
            (
                currentState == WorkflowState.PendingPDArchitectApproval.value
                && authenticatedUser.isInRole(UserRole.AWSPlatformDeliveryArchitect)
            ) ||
            (
                currentState == WorkflowState.PendingAwsConnectivityDetails.value
                && authenticatedUser.isInRole(UserRole.AWSConnectivityTechnicalImplementor)
            ) ||
            (
                currentState == WorkflowState.PendingTier0ArchitectApproval.value
                && authenticatedUser.isInRole(UserRole.AWSAccountTier0Approver)
            )
            ||
            (
                currentState == WorkflowState.PendingVaultTeamApproval.value
                && authenticatedUser.isInRole(UserRole.AWSAccountVaultApprover)
            )
        );
    }

    canHaveDMZDecision(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;
        return (
            (
                currentState === WorkflowState.PendingDmzImplementation.value
                && authenticatedUser.isInRole(UserRole.AWSConnectivityTechnicalImplementor)	
            )
        );
    }
    
    isCancellable(): boolean {
        if (this.isPendingRenewalApproval()) return false;
        return (
            (authenticatedUser.userId == this.workflowInstance?.createdBy?.id ||
            authenticatedUser.isInRole(UserRole.AWSAccountAdmin)) &&
            [
                WorkflowState.Draft.value,
                WorkflowState.PendingAccountOwnerApproval.value,
                WorkflowState.PendingPlatformsArchitectApproval.value,
                WorkflowState.PendingPDArchitectApproval.value,
                WorkflowState.PendingAwsConnectivityDetails.value,
                WorkflowState.Review.value,
                WorkflowState.AwaitingImplementation.value
            ].includes(this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value)
        );
    }

 
    canHaveOwnershipTransferDecision(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;
        
        if (currentState === WorkflowState.PendingCurrentAccountOwnerApproval.value) {
            return authenticatedUser.userId === this.owner?.id;
        }
        
        if (currentState === WorkflowState.PendingNewAccountOwnerApproval.value) {
            return authenticatedUser.userId === this.pendingOwnershipTransfer?.proposedOwner?.id;
        }
        
        return false;
    }

    canHaveGroupApprovalDecision(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;
        return (
            currentState === WorkflowState.PendingAccountGroupsApproval.value &&
            authenticatedUser.isInRole(UserRole.AWSAccountGroupsApprover)
        );
    }

    isCompleteAndDeletable() : boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;
        return ( 
            (currentState === WorkflowState.Completed.value &&
            (authenticatedUser.isInRole(UserRole.AWSAccountAdmin) || authenticatedUser.userId === this.owner?.id || authenticatedUser.userId === this.custodian?.id)) 
           
        );
    }

    isAwaitingOwnerApprovalForDeletion(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;
        return (
            currentState === WorkflowState.AwaitingOwnerDeletionApproval.value &&
            authenticatedUser.userId === this.owner?.id
        );
    }

    isExpired(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;
        return currentState === WorkflowState.Expired.value;
    }

    isExpiredAndRenewable(): boolean {
        if (!this.isExpired()) return false;
        return (
            authenticatedUser.isInRole(UserRole.AWSConnectivityTechnicalImplementor) ||
            authenticatedUser.isInRole(UserRole.AWSAccountAdmin)
        );
    }

    isExpiredAndDeletable(): boolean {
        if (!this.isExpired()) return false;
        return (
            authenticatedUser.isInRole(UserRole.AWSConnectivityTechnicalImplementor) ||
            authenticatedUser.isInRole(UserRole.AWSAccountAdmin)
        );
    }

    isCompletedAndRenewable(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;
        if (currentState !== WorkflowState.Completed.value) return false;
        return (
            authenticatedUser.userId === this.owner?.id ||
            (this.custodian && authenticatedUser.userId === this.custodian.id)
        );
    }

    isPocAccount(): boolean {
        return this.pocDurationDays != null && this.pocDurationDays > 0;
    }

    getPocExpirationDate(): Date | null {
        if (!this.pocStartDate || !this.pocDurationDays) return null;
        const start = new Date(this.pocStartDate);
        start.setDate(start.getDate() + this.pocDurationDays);
        return start;
    }

    getPocDaysRemaining(): number | null {
        const expiration = this.getPocExpirationDate();
        if (!expiration) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        expiration.setHours(0, 0, 0, 0);
        return Math.ceil((expiration.getTime() - today.getTime()) / 86400000);
    }


    isPendingRenewalApproval(): boolean {
        const currentState = this.workflowInstance?.currentWorkflowInstanceState;
        const state = currentState?.workflowState?.value;

        const isArchitectApprovalState =
            state === WorkflowState.PendingPDArchitectApproval.value ||
            state === WorkflowState.PendingPlatformsArchitectApproval.value;

        return isArchitectApprovalState && currentState?.action?.toLowerCase() === 'renew';
    }

    canDecideOnRenewal(): boolean {
        if (!this.isPendingRenewalApproval()) return false;

        const state = this.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value;

        return (
            (state === WorkflowState.PendingPlatformsArchitectApproval.value &&
                authenticatedUser.isInRole(UserRole.AWSPlatformArchitect)) ||
            (state === WorkflowState.PendingPDArchitectApproval.value &&
                authenticatedUser.isInRole(UserRole.AWSPlatformDeliveryArchitect))
        );

        return true;
    }
}
